import type { QuestTask, WorkLog } from '@aegislab/shared';
import { extractFreeMemo } from '../../lib/helpers';
import { getUtcDateString } from '@/shared/lib/format';

export interface HistoryFilter {
  readonly query: string;
  readonly from: string;
  readonly to: string;
}

/** 지난 일지 — 오늘 일지는 빼고 최신순. 검색어·기간이 있으면 맞는 날만 남긴다. */
export function historyEntries(
  logs: readonly WorkLog[],
  todayDate: string,
  { query, from, to }: HistoryFilter
): WorkLog[] {
  const needle = query.trim().toLocaleLowerCase('ko-KR');
  return logs
    .filter((log) => log.logDate < todayDate)
    .filter((log) => (!from || log.logDate >= from) && (!to || log.logDate <= to))
    .filter((log) => !needle || logText(log).includes(needle))
    .sort((a, b) => b.logDate.localeCompare(a.logDate));
}

function logText(log: WorkLog): string {
  return [extractFreeMemo(log.content), ...(log.questTasks ?? []).map(taskText)]
    .join('\n')
    .toLocaleLowerCase('ko-KR');
}

function taskText(task: QuestTask): string {
  return [task.title, task.memo, task.questTitle, task.checkpointTitle]
    .filter((text): text is string => !!text)
    .join('\n')
    .toLocaleLowerCase('ko-KR');
}

/**
 * 검색 중 그 날 보일 태스크 — 메모가 맞으면 그 날 전부, 아니면 맞는 태스크만(승인 시안과 같은 규칙).
 */
export function visibleTasks(log: WorkLog, query: string): QuestTask[] {
  const tasks = log.questTasks ?? [];
  const needle = query.trim().toLocaleLowerCase('ko-KR');
  if (!needle) return tasks;
  if (extractFreeMemo(log.content).toLocaleLowerCase('ko-KR').includes(needle)) return tasks;
  return tasks.filter((task) => taskText(task).includes(needle));
}

/** 하루 태스크를 퀘스트별로 묶는다 — 퀘스트가 없는 태스크는 한 묶음으로 모은다. 등장 순서 유지. */
export function groupByQuest(
  tasks: readonly QuestTask[]
): { key: string; title: string; tasks: QuestTask[] }[] {
  const groups = new Map<string, { key: string; title: string; tasks: QuestTask[] }>();
  for (const task of tasks) {
    const key = task.questId ?? 'free';
    const group = groups.get(key) ?? {
      key,
      title: task.questId ? (task.questTitle ?? '퀘스트') : '퀘스트 없는 태스크',
      tasks: [],
    };
    group.tasks.push(task);
    groups.set(key, group);
  }
  return [...groups.values()];
}

/** 태스크가 어떻게 끝났는지 — 사람이 닫지 않은 종결은 완료로 적지 않는다. */
export function taskStatusLabel(task: QuestTask): string {
  if (task.status === 'IN_PROGRESS') return '진행 중';
  switch (task.closeReason) {
    case 'CARRIED':
      return '하루 정리됨 · 완료 미확인';
    case 'SWEPT':
      return '미마감 정리됨 · 완료 미확인';
    case 'ROSTER_MOVED':
      return '로스터 이동으로 정리됨 · 완료 미확인';
    case 'CHECKOUT':
    case null:
    case undefined:
      return '완료';
  }
}

/** YYYY-MM-DD 하루 전 — 빠진 날 일지는 어제까지만 고른다. */
export function dayBefore(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 1);
  return getUtcDateString(d);
}
